import React, { useEffect, useState, useRef, useMemo } from "react";
import styles from "./LuckyWheel.module.scss";
import { strings } from "intl/fa";
import { useRequest } from "hooks/useRequest";
import { WheelSpinThunk } from "core/luckyWheel/luckyWheel.thunk";
import { PrizeType, PrizeTypeToString, WheelState } from "core/luckyWheel/luckyWheel.api";

interface Props {
  onEnd: (prize: number) => void;
}

const LuckyWheel = ({ onEnd }: Props) => {
  const prizes = Object.values(PrizeType)
    .filter((prize) => typeof prize === "number")
    .map((prize) => PrizeTypeToString(prize as number));

  const radius = useMemo(() => {
    const windowWidth = window.innerWidth;
    if (windowWidth < 768) {
      return 150;
    } else if (windowWidth < 1024) {
      return 250;
    } else {
      return window.innerHeight / 4;
    }
  }, [window.innerWidth]);

  const diameter = useMemo(() => radius * 2, [radius]);
  const segmentAngle = useMemo(() => 360 / prizes.length, [prizes.length]);
  const radians = useMemo(() => segmentAngle * (Math.PI / 180), [segmentAngle]);
  const segmentWidth = useMemo(() => 2 * radius * Math.sin(radians / 2), [radius, radians]);
  const segmentHeight = useMemo(() => radius * Math.cos(radians / 2), [radius, radians]);

  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const { sendRequest } = useRequest(WheelSpinThunk);
  const tickAudioRefs = useRef([
    new Audio("/LuckyWheel/tick3.mp3"),
    new Audio("/LuckyWheel/tick3.mp3"),
    new Audio("/LuckyWheel/tick3.mp3"),
  ]);
  const lastPlayedSegment = useRef<number>(-1);
  let audioIndex = 0;

  const playTick = (speed: number) => {
    const audio = tickAudioRefs.current[audioIndex];
    audio.currentTime = 0;
    audio.playbackRate = Math.max(0.5, speed / 5);
    audio.play().catch(() => {});
    audioIndex = (audioIndex + 1) % 3;
  };

  const spinWheel = async () => {
    if (spinning) return;
    setSpinning(true);

    sendRequest({
      onSuccess: (res) => {
        const prizeIndex = res ?? PrizeType.PrizeTypeEmpty;

        let prizeAngleRange = Math.random() * segmentAngle - segmentAngle / 2;
        prizeAngleRange = prizeAngleRange < 0 ? prizeAngleRange + 1 : prizeAngleRange - 1;

        let finalRotation = 2880 + (segmentAngle * prizeIndex + prizeAngleRange);
        smoothStop(finalRotation, prizeIndex);
      },
      onError: (err) => {
        setSpinning(false);
      },
    });
  };

  const smoothStop = (finalRotation: number, prizeIndex: number) => {
    let startRotation = rotation;
    let duration = 8000;
    let startTime = performance.now();

    const easeOut = (time: number) => {
      let elapsed = time - startTime;
      if (elapsed < duration) {
        let progress = elapsed / duration;
        let decelerationFactor = 1 - Math.pow(1 - progress, 3);
        let easedRotation = startRotation + (finalRotation - startRotation) * decelerationFactor;
        setRotation(easedRotation);

        let currentSegment = Math.floor(((easedRotation % 360) / segmentAngle) % prizes.length);
        let speed = (1 - progress) * 10;
        if (currentSegment !== lastPlayedSegment.current) {
          playTick(speed);
          lastPlayedSegment.current = currentSegment;
        }

        requestAnimationFrame(easeOut);
      } else {
        setRotation(finalRotation % 360);
        setSpinning(false);
        onEnd(prizeIndex);
      }
    };
    requestAnimationFrame(easeOut);
  };

  return (
    <div className={styles.wheel}>
      <div className={styles.prizes} style={{ width: diameter, height: diameter }}>
        {prizes.map((prize, index) => (
          <div
            key={index}
            className={styles.prizeWrapper}
            style={{
              transform: `rotate(${index * segmentAngle - rotation}deg) translateY(50%)`,
              width: `${segmentWidth}px`,
              willChange: "transform",
            }}
          >
            <div className={styles.prize} style={{ height: `${segmentHeight}px` }}>
              {prize}
            </div>
            <div className={styles.prizeBox}></div>
          </div>
        ))}
      </div>
      <div className={styles.blobs} style={{ width: radius * 2, height: radius * 2 }}>
        {Array.from({ length: 24 }).map((_, index) => (
          <div
            key={index}
            className={styles.blob}
            style={{
              transform: `rotate(${(360 / 24) * index}deg) translate(${radius - (window.innerWidth < 768 ? 5 : 7.5)}px)`,
              animationDelay: `${index * 100}ms`,
            }}
          />
        ))}
      </div>
      <button className={styles.spinButton} onClick={spinWheel}>
        {strings.festival.luckyWheel.button}
      </button>
      <div className={styles.pointer} />
    </div>
  );
};

export default LuckyWheel;
